import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, ExternalLink, CheckCircle2 } from "lucide-react";
import CouponClaim from "@/components/CouponClaim";

const CATEGORIES = ["Pelayanan", "Produk", "Harga", "Kebersihan", "Fasilitas", "Lainnya"];

const step = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 } };

export default function CustomerFeedback() {
  const { slug } = useParams();
  const [outlet, setOutlet] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [stage, setStage] = useState("select"); // select | positive | neutral | negative | done | done-positive
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [doneSat, setDoneSat] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/outlets/${slug}`);
        setOutlet(data);
      } catch { setNotFound(true); }
    })();
  }, [slug]);

  const brand = outlet?.brand_color || "#059669";

  const submit = async (satisfaction, opts = {}) => {
    if (submitting) return;
    const skip = opts.skip === true;
    const submittedName = skip ? "" : name.trim();
    const submittedWa = skip ? "" : whatsapp.trim();
    // Enforce: if user partially fills contact, require both
    if (!skip && ((submittedName && !submittedWa) || (!submittedName && submittedWa))) {
      toast.error("Nama dan nomor WhatsApp harus diisi jika Anda ingin mendapatkan informasi promo.");
      return;
    }
    if (submittedWa && !/^[0-9+\-\s]{8,20}$/.test(submittedWa)) {
      toast.error("Nomor WhatsApp tidak valid");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        outlet_slug: slug,
        satisfaction,
        name: submittedName,
        whatsapp: submittedWa,
        category: opts.category ?? category,
        message: opts.message ?? message,
      };
      await api.post("/public/feedback", body);
      setDoneSat(satisfaction);
      setStage(satisfaction === "positive" ? "done-positive" : "done");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengirim");
    } finally { setSubmitting(false); }
  };

  if (notFound) return (
    <div className="min-h-screen grid place-items-center px-6 text-center">
      <div>
        <div className="text-2xl font-bold mb-2">Outlet tidak ditemukan</div>
        <div className="text-sm text-zinc-500">Periksa kembali URL atau QR code Anda.</div>
      </div>
    </div>
  );

  if (!outlet) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Memuat...</div>;

  if (outlet.is_active === false) return (
    <div className="min-h-screen grid place-items-center px-6 text-center" data-testid="outlet-inactive-message">
      <div>
        <div className="text-2xl font-bold mb-2">Halaman tidak tersedia</div>
        <div className="text-sm text-zinc-500">Outlet ini sedang tidak menerima feedback.</div>
      </div>
    </div>
  );

  const emojis = [
    { key: "positive", emoji: "😊", label: "PUAS", sub: "Pengalaman menyenangkan", bg: "bg-emerald-50 border-emerald-200 hover:border-emerald-400", accent: "text-emerald-700" },
    { key: "neutral", emoji: "😐", label: "BIASA SAJA", sub: "Ada yang bisa diperbaiki", bg: "bg-amber-50 border-amber-200 hover:border-amber-400", accent: "text-amber-700" },
    { key: "negative", emoji: "😞", label: "KURANG PUAS", sub: "Ada masalah yang perlu ditangani", bg: "bg-red-50 border-red-200 hover:border-red-400", accent: "text-red-700" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white">
      <div className="max-w-md mx-auto min-h-screen px-4 py-8 flex flex-col">
        <header className="text-center space-y-3 pt-2">
          {outlet.logo_url
            ? <img src={outlet.logo_url} alt={outlet.name} className="w-20 h-20 mx-auto rounded-2xl object-cover border border-zinc-200 shadow-sm" data-testid="outlet-logo" />
            : <div className="w-20 h-20 mx-auto rounded-2xl grid place-items-center text-white text-2xl font-bold shadow-sm" style={{ backgroundColor: brand }}>{outlet.name[0]}</div>}
          <h1 className="text-2xl font-extrabold tracking-tight" data-testid="outlet-name">{outlet.name}</h1>
          {outlet.address && <div className="text-xs text-zinc-500">{outlet.address}</div>}
        </header>

        <AnimatePresence mode="wait">
          {stage === "select" && (
            <motion.section key="select" {...step} transition={{ duration: 0.25 }} className="mt-8 flex-1">
              <h2 className="text-center text-lg font-semibold mb-5">Bagaimana pengalaman Anda hari ini?</h2>
              <div className="space-y-3">
                {emojis.map(e => (
                  <button key={e.key} onClick={() => setStage(e.key)}
                    data-testid={`emoji-button-${e.key}`}
                    className={`emoji-btn tap-target w-full rounded-2xl border-2 p-4 flex items-center gap-4 shadow-sm ${e.bg}`}>
                    <span className="text-4xl">{e.emoji}</span>
                    <div className="flex-1 text-left">
                      <div className={`font-bold tracking-wide ${e.accent}`}>{e.label}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{e.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.section>
          )}

          {stage === "positive" && (
            <motion.section key="pos" {...step} transition={{ duration: 0.25 }} className="mt-8 space-y-5">
              <div className="text-center">
                <div className="text-5xl mb-2">🎉</div>
                <h2 className="text-xl font-bold">Terima kasih!</h2>
                <p className="text-sm text-zinc-500 mt-1">Senang bisa melayani Anda.</p>
              </div>
              {outlet.google_review_url && (
                <div className="bg-white border border-zinc-200 rounded-2xl p-4 text-center">
                  <p className="text-sm text-zinc-700">Jika Anda berkenan, bantu kami dengan memberikan ulasan di Google.</p>
                  <a href={outlet.google_review_url} target="_blank" rel="noreferrer">
                    <Button className="mt-3 w-full tap-target bg-emerald-600 hover:bg-emerald-700" data-testid="google-review-cta">
                      <Star size={16} className="mr-2" /> Beri Ulasan di Google <ExternalLink size={14} className="ml-2" />
                    </Button>
                  </a>
                </div>
              )}
              <div className="bg-white border border-zinc-200 rounded-2xl p-4 space-y-3">
                <div className="text-sm font-semibold">Mau mendapatkan informasi promo dari kami?</div>
                <div className="space-y-1.5">
                  <Label htmlFor="pos-name">Nama</Label>
                  <Input id="pos-name" value={name} onChange={e => setName(e.target.value)} data-testid="pos-name-input" placeholder="Nama Anda" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pos-wa">Nomor WhatsApp</Label>
                  <Input id="pos-wa" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} data-testid="pos-whatsapp-input" placeholder="08xxxxxxxxxx" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => submit("positive")} disabled={submitting} className="flex-1 bg-emerald-600 hover:bg-emerald-700 tap-target" data-testid="pos-submit-button">
                    Simpan
                  </Button>
                  <Button variant="outline" onClick={() => submit("positive", { message: "", category: "", skip: true })} disabled={submitting} className="tap-target" data-testid="pos-skip-button">
                    Lewati
                  </Button>
                </div>
              </div>
            </motion.section>
          )}

          {(stage === "neutral" || stage === "negative") && (
            <motion.section key={stage} {...step} transition={{ duration: 0.25 }} className="mt-8 space-y-4">
              <div>
                <h2 className="text-lg font-bold">
                  {stage === "neutral" ? "Terima kasih atas masukannya." : "Maaf pengalaman Anda belum sesuai harapan."}
                </h2>
                <p className="text-sm text-zinc-500 mt-1">
                  {stage === "neutral" ? "Apa yang bisa kami perbaiki?" : "Kami ingin memperbaikinya."}
                </p>
              </div>
              <div>
                <Label>Kategori</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {CATEGORIES.map(c => (
                    <button key={c} onClick={() => setCategory(c)} type="button"
                      data-testid={`category-${c.toLowerCase()}`}
                      className={`px-3 py-2.5 text-sm rounded-xl border transition-colors ${category === c ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-zinc-200 hover:border-emerald-400"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="msg">{stage === "neutral" ? "Ceritakan pengalaman Anda" : "Ceritakan masalah Anda"}</Label>
                <Textarea id="msg" rows={4} value={message} onChange={e => setMessage(e.target.value)} data-testid="feedback-message-input"
                  placeholder="Tulis pengalaman Anda..." />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="name">Nama</Label>
                <Input id="name" value={name} onChange={e => setName(e.target.value)} data-testid="feedback-name-input" placeholder="Nama Anda" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wa">Nomor WhatsApp</Label>
                <Input id="wa" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} data-testid="feedback-whatsapp-input" placeholder="08xxxxxxxxxx" />
              </div>
              <Button onClick={() => submit(stage)} disabled={submitting} className="w-full tap-target bg-emerald-600 hover:bg-emerald-700"
                data-testid="feedback-submit-button">
                {submitting ? "Mengirim..." : (stage === "neutral" ? "Kirim Feedback" : "Kirim Keluhan")}
              </Button>
            </motion.section>
          )}

          {(stage === "done" || stage === "done-positive") && (
            <motion.section key="done" {...step} transition={{ duration: 0.3 }} className="mt-10 text-center space-y-3" data-testid="feedback-success">
              <CheckCircle2 size={64} className="mx-auto text-emerald-600" />
              <h2 className="text-2xl font-bold">
                {stage === "done-positive" ? (outlet.thank_you_message || "Terima kasih!") : "Terima kasih sudah memberi tahu kami."}
              </h2>
              <p className="text-sm text-zinc-500">
                {stage === "done-positive" ? "Sampai jumpa lagi." : "Tim kami akan menindaklanjutinya."}
              </p>
              {doneSat && <CouponClaim slug={slug} satisfaction={doneSat} name={name.trim()} whatsapp={whatsapp.trim()} />}
            </motion.section>
          )}
        </AnimatePresence>

        <footer className="pt-8 pb-4 text-center text-[10px] uppercase tracking-wider text-zinc-400">
          Didukung oleh JagaOmzet
        </footer>
      </div>
    </div>
  );
}
